import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'

import { getLocalDate } from 'lib/datetime/calendarUtils'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Group from 'components/utils/Group'

const CellWrapper = styled(Clickable)`
  display: flex;
  text-align: left;
  position: relative;
  overflow: hidden;
  align-items: stretch;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  transition: background-color 0.2s, color 0.2s;

  &:disabled {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
  &.check-in, &.check-out {
    box-shadow: none;
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.between-day {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground}
  }
`

const DateText = styled.div`
  width: ${rem(72)};
  height: ${rem(60)};
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const DayOfWeek = styled.abbr`
  margin-right: ${rem(4)};
`

const Main = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  height: 100%;
  padding: ${rem(8)};

  &.row-direction {
    flex-wrap: wrap;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }
`

const CheckInOutText = styled(BodyText)`
  position: absolute;
  right: ${rem(16)};
`

const RightBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`

const PlusFlightsText = styled(Caption)`
  margin-left: ${rem(4)};
`

interface Props {
  isCheckIn?: boolean
  isCheckOut?: boolean
  duration: number
  day: number
  hasPromo: boolean
  price?: number
  propertyFees?: number
  flightPrice?: number
  checkIn: string
  style?: React.CSSProperties
  className?: string
  isBetweenDay?: boolean
  flights: boolean
  adultsNumber: number
  roomsNumber: number
  displayPricingAsPerPerson: boolean
  onClick: () => void
}

function CalendarSidebarDayCell(props: Props) {
  const intl = useIntl()
  const {
    duration,
    day,
    price,
    propertyFees,
    checkIn,
    isCheckIn,
    isCheckOut,
    style,
    className,
    isBetweenDay,
    hasPromo,
    flights,
    flightPrice,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
    onClick,
  } = props

  const date = getLocalDate(checkIn)
  const greyDarkTextType = !isCheckIn ? 'neutral-three' : undefined

  const onCellClick = useCallback(() => {
    if (isCheckIn) { return }
    onClick?.()
  }, [onClick, isCheckIn])

  const hotelPrice = price ?? 0
  const hotelPropertyFees = propertyFees ?? 0

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: hotelPrice + hotelPropertyFees,
    adultsNumber,
    roomsNumber,
    nights: duration,
    withFlights: !!flights,
    flightPrice: flightPrice ?? 0,
    displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
  })

  const nightsPlusFlightsFromLabel = <FormattedMessage
    id="3etfC32hHD"
    defaultMessage="{nights, plural, one {# night} other {# nights}} + flights from"
    description="Bedbank calendar sidebar day cell: nights plus flights from label"
    values={{ nights: duration }}
  />
  const nightsFromLabel = <FormattedMessage
    id="J6FRmA39sc"
    defaultMessage="{nights, plural, one {# night} other {# nights}} from"
    description="Bedbank calendar sidebar day cell: nights from label"
    values={{ nights: duration }}
  />
  const plusFlightsLabel = intl.formatMessage({
    id: 'pF6oj0qk/U',
    defaultMessage: '+ flights',
    description: 'Suffix indicating flights price is additional on bedbank calendar sidebar day cell',
  })

  return (
    <CellWrapper
      style={style}
      className={clsx(className, {
        'check-in': isCheckIn,
        'check-out': isCheckOut,
        'between-day': isBetweenDay,
      })}
      onClick={onCellClick}
    >
      <DateText>
        <Heading as="span" variant="heading6">
          <DayOfWeek title={date.toLocaleDateString('en-GB', { weekday: 'long' })}>
            {date.toLocaleDateString('en-GB', { weekday: 'short' })}
          </DayOfWeek>
          <span>{day}</span>
        </Heading>
        <CheckInOutText variant="small" weight="bold">
          {isCheckIn &&
            intl.formatMessage({
              id: 'R+rsU0f9vI',
              defaultMessage: 'Check in',
              description: 'Check-in label for bedbank calendar day cell in the calendar picker',
            })}
          {isCheckOut &&
            intl.formatMessage({
              id: 'getsD9cmie',
              defaultMessage: 'Check out',
              description: 'Check-out label for the bedbank calendar day cell on the offer detail page',
            })}
        </CheckInOutText>
      </DateText>
      {!isCheckOut && !isBetweenDay &&
        <Main>
          <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
            <Group direction="vertical">
              {hasPromo && !!total && <div>
                <Caption variant="medium" colour={greyDarkTextType}>
                  {flights && flightPrice ? nightsPlusFlightsFromLabel : nightsFromLabel}
                </Caption>
                <BodyText as="span" variant="large" weight="bold" data-testid="day-price">
                  <BodyText colour={!isCheckIn ? 'highlight-secondary' : undefined} as="span" variant="large" weight="bold">
                    <FormatCurrency value={total} />
                    {displayPricingAsPerPerson && <Caption variant="medium" as="span">{saleUnit}</Caption>}
                  </BodyText>
                  {!displayPricingAsPerPerson && <Caption variant="medium" weight="normal" as="span"> {saleUnit}</Caption>}
                </BodyText>
                {flights && !flightPrice &&
                  <PlusFlightsText
                    variant="medium"
                    weight="bold"
                    forwardedAs="span"
                  >
                    {plusFlightsLabel}
                  </PlusFlightsText>}
              </div>}
              {(!hasPromo || !total) && !isCheckIn && <Caption variant="medium" colour={greyDarkTextType} underline>{intl.formatMessage({
                id: 'VTlKIIDRbo',
                defaultMessage: 'Select to view prices',
                description: 'Prompt shown on bedbank calendar sidebar day cell when no promo pricing is available',
              })}
              </Caption>}
            </Group>
            {hasPromo && !isBetweenDay && !isCheckIn && !isCheckOut && <RightBlock><SolidFireIcon size="XS" colour="urgency" /></RightBlock>}
          </Group>
        </Main>}
    </CellWrapper>
  )
}

export default React.memo(CalendarSidebarDayCell)
